// src/pages/auth/ResetPassword.tsx
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { errorHandler, generateStrongPassword } from "@/lib/utils";
import { authService } from "@/services/api";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { Copy, Eye, EyeOff } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useSearchParams } from "react-router";
import { z } from "zod";

const formSchema = z
  .object({
    email: z.string().email("Email inválido"),
    password: z
      .string()
      .min(8, "A senha deve ter pelo menos 8 caracteres")
      .regex(/[a-z]/, "A senha deve conter ao menos uma letra minúscula")
      .regex(/[A-Z]/, "A senha deve conter ao menos uma letra maiúscula")
      .regex(/[0-9]/, "A senha deve conter ao menos um número")
      .regex(/[^a-zA-Z0-9]/, "A senha deve conter ao menos um símbolo"),
    password_confirmation: z.string(),
  })
  .refine((data) => data.password === data.password_confirmation, {
    message: "As senhas não coincidem",
    path: ["password_confirmation"],
  });

type FormValues = z.infer<typeof formSchema>;

export function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const emailParam = searchParams.get("email") ?? "";
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState(false);

  const resetPassword = useMutation({
    mutationFn: (payload: FormValues & { token: string }) =>
      authService.resetPassword(payload),
    onSuccess: () => setTimeout(() => navigate("/auth/login"), 3000),
    onError: (error) =>
      errorHandler(error, "Ocorreu um erro ao redefinir sua senha."),
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: emailParam,
      password: "",
      password_confirmation: "",
    },
  });

  const handleGeneratePassword = () => {
    const password = generateStrongPassword();
    form.setValue("password", password, {
      shouldValidate: true,
      shouldDirty: true,
    });
    form.setValue("password_confirmation", password, {
      shouldValidate: true,
      shouldDirty: true,
    });
    setShowPassword(true);
    setCopied(false);
  };

  const handleCopyPassword = async () => {
    const password = form.getValues("password");
    if (!password) return;

    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(password);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch {
      // ponytail: clipboard permission can be denied silently, no fallback UI needed
    }
  };

  const handleSubmit = (values: FormValues) => {
    if (!token) return;

    resetPassword.mutate({ ...values, token });
  };

  if (!token) {
    return (
      <div className="text-center space-y-4">
        <h2 className="text-2xl font-bold text-gray-900">Link inválido</h2>
        <p className="text-gray-600">
          Este link de redefinição de senha é inválido ou expirou. Solicite um
          novo link para continuar.
        </p>
        <Link
          to="/auth/forgot-password"
          className="block text-primary hover:underline"
        >
          Solicitar novo link
        </Link>
      </div>
    );
  }

  if (resetPassword.isSuccess) {
    return (
      <div className="text-center space-y-4">
        <h2 className="text-2xl font-bold text-gray-900">Senha Redefinida</h2>
        <p className="text-gray-600">
          Sua senha foi redefinida com sucesso. Você será redirecionado para a
          página de login em alguns segundos.
        </p>
        <Link to="/auth/login" className="block text-primary hover:underline">
          Ir para o login
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-center text-gray-900">
          Redefinir Senha
        </h2>
        <p className="mt-2 text-sm text-gray-600 text-center">
          Digite sua nova senha.
        </p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input type="email" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-center justify-between">
                  <FormLabel>Nova senha</FormLabel>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleGeneratePassword}
                  >
                    Gerar senha forte
                  </Button>
                </div>
                <FormControl>
                  <div className="relative">
                    <Input
                      {...field}
                      type={showPassword ? "text" : "password"}
                      autoComplete="new-password"
                      className="pr-20"
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center gap-1 pr-2">
                      <button
                        type="button"
                        onClick={handleCopyPassword}
                        className="text-gray-600 hover:text-gray-900"
                        tabIndex={-1}
                      >
                        <Copy
                          className={
                            "h-4 w-4 " + (copied ? "text-green-600" : "")
                          }
                        />
                        <span className="sr-only">
                          {copied ? "Senha copiada" : "Copiar senha"}
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowPassword((prev) => !prev)}
                        className="text-gray-600 hover:text-gray-900"
                        tabIndex={-1}
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                        <span className="sr-only">
                          {showPassword ? "Esconder senha" : "Mostrar senha"}
                        </span>
                      </button>
                    </div>
                  </div>
                </FormControl>
                <p className="text-xs text-gray-500">
                  Mínimo de 8 caracteres, com letra maiúscula, minúscula, número
                  e símbolo.
                </p>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password_confirmation"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Confirme a nova senha</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button
            type="submit"
            disabled={resetPassword.isPending}
            className="w-full"
          >
            {resetPassword.isPending ? "Redefinindo..." : "Redefinir Senha"}
          </Button>
        </form>
      </Form>

      <div className="text-center">
        <Link to="/auth/login" className="text-sm text-primary hover:underline">
          Voltar para o login
        </Link>
      </div>
    </div>
  );
}
